// AiReviewClient — thin contract used by WP-9 AI review surfaces.
//
// The acceptance tests in
// `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §8.9 say
// approvals must call `client.ai.approve(candidateId)` (mock), rejections
// must call `client.ai.reject(candidateId)`, and `publish` only fires after
// the confirmation dialog. This module declares the interface and ships a
// no-op default mock so defence-mode builds do not start any external
// traffic and so tests can substitute a recording client.
//
// Real persistence / audit forwarding is a platform dependency tracked in
// the masterplan §11 ("platform SDK").

import type { CandidateFact } from "@/app/scenePackage";

/** Result of an action. Successful by default; tests use `ok: false` to
 *  simulate failure paths without exceptions. */
export interface AiClientResult {
  readonly ok: boolean;
  readonly error?: string;
}

/** Search query against the semantic graph. Returned shape kept narrow so
 *  the UI does not depend on the platform graph yet. */
export interface AiSemanticSearchHit {
  readonly nodeId: string;
  readonly label: string;
  readonly factId?: string;
  readonly snippet?: string;
}

export interface AiPublishRequest {
  readonly candidateIds: readonly string[];
  /** Optional reason captured from the confirmation dialog; not used by
   *  the mock but kept on the contract so the future real client can
   *  forward it to the audit log. */
  readonly reason?: string;
}

export interface AiReviewClient {
  approve(candidateId: string): Promise<AiClientResult>;
  reject(candidateId: string): Promise<AiClientResult>;
  publish(request: AiPublishRequest): Promise<AiClientResult>;
  /** Optional — the masterplan §7.9 calls for "search semantic graph"
   *  inside the card. The card uses this when present, falls back to
   *  pure local filtering when absent. */
  search?(query: string): Promise<readonly AiSemanticSearchHit[]>;
}

/**
 * Default no-op AiReviewClient. Returns `{ ok: true }` for every action.
 * Used as the safe default when no platform SDK is wired in (mirrors the
 * Phase-9 "local/mock" decision in
 * `docs/results/PHASE-9-AI-ENRICHMENT-HUMAN-REVIEW-COMPLETION.md`).
 */
export function createMockAiReviewClient(): AiReviewClient {
  return {
    async approve() {
      return { ok: true };
    },
    async reject() {
      return { ok: true };
    },
    async publish() {
      return { ok: true };
    },
  };
}

/** Recording client used by tests; returns the same successful default
 *  but stores every invocation so assertions can read them. */
export interface RecordingAiReviewClient extends AiReviewClient {
  readonly calls: readonly AiClientCall[];
}

export type AiClientCall =
  | { kind: "approve"; candidateId: string }
  | { kind: "reject"; candidateId: string }
  | { kind: "publish"; request: AiPublishRequest }
  | { kind: "search"; query: string };

/** Construct a recording client. Tests use this to assert which method
 *  was invoked and with what arguments. The `nextResult` knob lets tests
 *  simulate failure paths without throwing. */
export function createRecordingAiReviewClient(options?: {
  nextResult?: AiClientResult;
  searchHits?: readonly AiSemanticSearchHit[];
}): RecordingAiReviewClient {
  const calls: AiClientCall[] = [];
  const result: AiClientResult = options?.nextResult ?? { ok: true };
  const searchHits = options?.searchHits ?? [];
  return {
    calls,
    async approve(candidateId) {
      calls.push({ kind: "approve", candidateId });
      return result;
    },
    async reject(candidateId) {
      calls.push({ kind: "reject", candidateId });
      return result;
    },
    async publish(request) {
      calls.push({ kind: "publish", request });
      return result;
    },
    async search(query) {
      calls.push({ kind: "search", query });
      return searchHits;
    },
  };
}

/** Compile-time helper so callers can narrow `CandidateFact` arrays
 *  destined for `publish`. */
export function candidateIdsForPublish(
  candidates: readonly CandidateFact[],
): string[] {
  return candidates.map((candidate) => candidate.id);
}
